import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  useLazyViewAdminConfigurationQuery,
  useUpdateAdminConfigurationMutation,
} from "../../../redux/api/settings/settingsAPI";
import { useLazyViewBranchesAdminQuery } from "../../../redux/api/solar-estimation/solarEstimationAPI";
import {
  useLazyViewUserConfigurationQuery,
  useUpdateUserConfigurationMutation,
} from "../../../redux/api/user/userAPI";
import {
  selectTheme,
  setTheme,
} from "../../../redux/features/theme/theme-slice";
import { selectProfile } from "../../../redux/features/user/user-slice";
import AdminBranchesViewResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/admin-branches-view-response-dto";
import { ViewAdminConfigurationResponseDTO } from "../../../shared/oversight-core/dtos/response-dtos/view-admin-configuration-response-dto";
import { EConfigurationType } from "../../../shared/oversight-core/enums/configuration-type";
import { EConfigurations } from "../../../shared/oversight-core/enums/configurations";
import { EFeatureType } from "../../../shared/oversight-core/enums/feature-type";
import { UserRole } from "../../../shared/oversight-core/enums/user-roles";
import { IBranchView } from "../../../shared/oversight-core/interfaces/branch-view";
import { IBrandPriority } from "../../../shared/oversight-core/interfaces/brand-priority";
import { Configurations } from "../../../shared/oversight-core/interfaces/configurations";
import SettingItem from "../../../shared/oversight-core/shared-components/setting-item/setting-item";
import AppSwitch from "../../../shared/oversight-core/ui-elements/app-switch/app-switch";
import AppTabs from "../../../shared/oversight-core/ui-elements/app-tabs/app-tabs";
import AppButton from "../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import MaterialIcon from "../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import Search from "../../../shared/oversight-core/ui-elements/search/search";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import { configurationDescription } from "../../../shared/oversight-core/utils/configuration-description";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/oversight-core/utils/toast";
import translate from "../../../shared/oversight-core/utils/translate";
import BrandPriorityChangeModal from "../../build-system/components/brand-priority-change-modal/brand-priority-change-modal";
import AddBranchModal from "./add-branch-modal/add-branch-modal";
import DeleteBranchModal from "./delete-branch-modal/delete-branch-modal";
import styles from "./settings.module.scss";

interface IExtendedBrandPriority extends IBrandPriority {
  id: number;
}

const Settings = () => {
  const profile = useSelector(selectProfile);
  const theme = useSelector(selectTheme);
  const dispatch = useDispatch();

  const [selectedItem, setSelectedItem] = useState<{
    itemName: string;
    id: number;
  }>({
    itemName: "Solar Recommendation",
    id: 1,
  });
  const [showBrandPriorityModal, setShowBrandPriorityModal] = useState(false);
  const [configurations, setConfigurations] = useState<Configurations>(
    {} as Configurations,
  );
  const [updateCurrentState, setUpdateCurrentState] = useState(false);
  const [brands, setBrands] = useState<IExtendedBrandPriority[]>([]);
  const [selectedTab, setSelectedTab] = useState(1);
  const [showAddBranchModal, setShowAddBranchModal] = useState(false);
  const [branches, setBranches] = useState<IBranchView[]>([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [showDeleteBranchModal, setShowDeleteBranchModal] = useState(false);
  const [branchId, setBranchId] = useState("");
  const [isUpdateCurrentState, setIsUpdateCurrentState] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const [
    triggerViewAdminConfiguration,
    { isFetching: isFetchingAdminConfiguration },
  ] = useLazyViewAdminConfigurationQuery();
  const [
    triggerViewUserConfiguration,
    { isFetching: isFetchingUserConfiguration },
  ] = useLazyViewUserConfigurationQuery();
  const [
    triggerUpdateAdminConfiguration,
    { isLoading: isLoadingAdminConfiguration },
  ] = useUpdateAdminConfigurationMutation();
  const [
    triggerUpdateUserConfiguration,
    { isLoading: isLoadingUserConfiguration },
  ] = useUpdateUserConfigurationMutation();
  const [triggerViewBranchesAdmin, { isFetching: isFetchingBranches }] =
    useLazyViewBranchesAdminQuery();

  useEffect(() => {
    switch (selectedTab) {
      case 1:
        return setSelectedCountryCode("KE");
      case 2:
        return setSelectedCountryCode("TZ");
      case 3:
        return setSelectedCountryCode("UG");
    }
  }, [selectedTab]);

  useEffect(() => {
    if (selectedItem.id === 2) {
      triggerViewBranchesAdmin({
        pageNumber: 0,
        pageSize: 1000,
        searchValue: searchValue,
      })
        .unwrap()
        .then((res: AdminBranchesViewResponseDTO) => {
          setBranches(res.page.elements);
        })
        .catch(() => setBranches([]));
    }
  }, [
    triggerViewBranchesAdmin,
    selectedItem.id,
    isUpdateCurrentState,
    searchValue,
  ]);

  useEffect(() => {
    (profile?.role === UserRole.ROLE_USER
      ? triggerViewUserConfiguration
      : triggerViewAdminConfiguration)({
      configurationType:
        profile?.role === UserRole.ROLE_USER
          ? EConfigurationType.ROLE_BASED
          : EConfigurationType.SYSTEM_BASED,
    })
      .unwrap()
      .then((res: ViewAdminConfigurationResponseDTO) => {
        setConfigurations(
          res?.configurations?.configurations || ({} as Configurations),
        );
      })
      .catch(() => {
        setConfigurations({} as Configurations);
      });
  }, [triggerViewAdminConfiguration, updateCurrentState, profile?.role]);

  useEffect(() => {
    const list = configurations?.BRAND_PRIORITY_LIST;
    setBrands(
      Array.isArray(list)
        ? list.map(
            (bp, index): IExtendedBrandPriority => ({
              ...bp,
              id: index,
            }),
          )
        : [],
    );
  }, [configurations]);

  const onPreferenceChange = (
    configuration: EConfigurations,
    newValue: boolean | IBrandPriority[],
  ) => {
    const updatedConfigurations = Object.fromEntries(
      Object.entries(configurations || {}).map(([key, value]) => [
        key,
        key === configuration ? newValue : value,
      ]),
    );

    setConfigurations(updatedConfigurations as Configurations);
  };

  const onSaveConfiguration = async () => {
    if (profile?.role) {
      await (
        profile?.role === UserRole.ROLE_USER
          ? triggerUpdateUserConfiguration
          : triggerUpdateAdminConfiguration
      )({
        configurationType:
          profile.role === UserRole.ROLE_USER
            ? EConfigurationType.ROLE_BASED
            : EConfigurationType.SYSTEM_BASED,
        featureType: EFeatureType.SOLAR_SIM,
        role: profile.role,
        configurations: configurations,
      })
        .unwrap()
        .then(() => {
          showSuccessMessage("Configuration updated successfully");
          setUpdateCurrentState((ps) => !ps);
        })
        .catch(() => {
          showErrorMessage("Failed to update configuration");
        });
    }
  };

  const onSearch = () => {
    if (searchText) {
      setSearchValue(searchText);
    }
  };

  useEffect(() => {
    if (!searchText) {
      setSearchValue("");
    }
  }, [searchText]);

  return (
    <>
      <div className={`${styles.container} px-2 px-lg-4 position-relative`}>
        <Row>
          <Col
            className={`col-3 col-xl-2 ${styles.settingItemContainer} d-none d-lg-block`}
          >
            <div className={styles.settingItems}>
              {profile?.role === UserRole.ROLE_USER ? (
                <SettingItem
                  items={[
                    { itemName: "Solar Recommendation", id: 1 },
                    { itemName: "Theme", id: 3 },
                  ]}
                  onChange={(item) => setSelectedItem(item)}
                  selectedItemId={selectedItem.id}
                />
              ) : (
                <SettingItem
                  items={[
                    { itemName: "Solar Recommendation", id: 1 },
                    { itemName: "Branches", id: 2 },
                    { itemName: "Theme", id: 3 },
                  ]}
                  onChange={(item) => setSelectedItem(item)}
                  selectedItemId={selectedItem.id}
                />
              )}
            </div>
          </Col>
          <Col>
            <div className={styles.settings}>
              {selectedItem.id === 2 && (
                <Row className="justify-content-start">
                  <Col className="col-12 col-sm-7 col-xl-4">
                    <Search
                      onSearch={onSearch}
                      handleSearchChange={(searchText) => {
                        setSearchText(searchText);
                      }}
                      value={searchText}
                      placeholder="Search Branch"
                    />
                  </Col>
                </Row>
              )}
              <Row
                className={`align-items-center ${
                  selectedItem.id === 1 ? `` : `mt-4`
                }`}
              >
                <Col className="font-weight-500 font-size-24 title-2">
                  {selectedItem.id === 1
                    ? `Solar Recommendation Engine Rules`
                    : selectedItem.itemName}
                </Col>
                {selectedItem.id === 1 && (
                  <Col className="col-auto">
                    <AppButton
                      text="Save"
                      className="px-5 font-size-14"
                      onClick={onSaveConfiguration}
                      isLoading={
                        isLoadingAdminConfiguration ||
                        isLoadingUserConfiguration
                      }
                    />
                  </Col>
                )}
              </Row>
              <div className={styles.hr}></div>
              {selectedItem.id === 1 && (
                <>
                  <Row>
                    <Col className="title-3 text-size-16 text-weight-500 mt-3">
                      System Design Preferences
                    </Col>
                  </Row>
                  {Object.entries(configurations || {}).map(
                    ([configuration, value]) => {
                      if (
                        configuration === EConfigurations.BRAND_PRIORITY_LIST
                      ) {
                        return null;
                      }

                      return (
                        <Row key={configuration} className="my-4">
                          {configuration ===
                            EConfigurations.OPTIMIZE_FOR_COST && (
                            <Row>
                              <Col className="title-3 text-size-16 text-weight-500 my-3">
                                Performance Preferences
                              </Col>
                            </Row>
                          )}
                          <Row className="align-items-center">
                            <Col className="col-auto title-2 font-weight-500 pe-0">
                              {translate(configuration)}
                            </Col>
                            <Col className="col-auto">
                              <AppSwitch
                                isOn={typeof value === "boolean" && value}
                                onSwitch={(value: boolean) => {
                                  onPreferenceChange(
                                    configuration as EConfigurations,
                                    value,
                                  );
                                }}
                                size="sm"
                              />
                            </Col>
                          </Row>
                          <Row className="my-2">
                            <Col className="text-color-2 font-size-12">
                              {configurationDescription(
                                configuration as EConfigurations,
                              )}
                            </Col>
                          </Row>
                          {configuration === EConfigurations.BRAND_PRIORITY && (
                            <Row>
                              <Col
                                className="text-color-2 font-size-12 text-decoration-underline cursor-pointer"
                                onClick={() => {
                                  setShowBrandPriorityModal(true);
                                }}
                              >
                                Change Brand Priority
                              </Col>
                            </Row>
                          )}
                        </Row>
                      );
                    },
                  )}
                </>
              )}
              {selectedItem.id === 2 && (
                <>
                  <Row className="mt-3 align-items-center justify-content-between">
                    <Col className="col-auto">
                      <AppTabs
                        buttons={[
                          {
                            buttonName: "Kenya",
                            selectedColor: "blueColor",
                            id: 1,
                          },
                          {
                            buttonName: "Tanzania",
                            selectedColor: "blueColor",
                            id: 2,
                          },
                          {
                            buttonName: "Uganda",
                            selectedColor: "blueColor",
                            id: 3,
                          },
                        ]}
                        onChange={(id) => setSelectedTab(id)}
                        selectedTabId={selectedTab}
                      />
                    </Col>
                    <Col className="col-auto">
                      <AppButton
                        text="Add Branch"
                        className="rounded-3 font-size-12 font-weight-500"
                        onClick={() => {
                          setShowAddBranchModal(true);
                          setBranchId("");
                        }}
                      />
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col>
                      {branches &&
                      branches.filter(
                        (i) => i.countryCode === selectedCountryCode,
                      ).length > 0 ? (
                        branches
                          .filter((i) => i.countryCode === selectedCountryCode)
                          .map((branch) => {
                            return (
                              <Row
                                key={branch.identity}
                                className={`align-items-center theme-card p-2 mx-0 my-2 ${styles.branchCardContainer}`}
                              >
                                <Col>
                                  <Row>
                                    <Col className="title-2 font-weight-500 font-size-16">
                                      {branch.name}
                                    </Col>
                                  </Row>
                                  <Row>
                                    <Col className="text-color-2 font-weight-400 font-size-14">
                                      {`Latitude - ${branch.latitude}`}
                                      &nbsp;|&nbsp;
                                      {`Longitude - ${branch.longitude}`}{" "}
                                    </Col>
                                  </Row>
                                </Col>
                                <Col className="col-auto">
                                  <Row>
                                    <Col>
                                      <MaterialIcon
                                        icon="border_color"
                                        color="var(--color-edit-icon)"
                                        size={20}
                                        className="cursor-pointer"
                                        onClick={() => {
                                          setShowAddBranchModal(true);
                                          setBranchId(branch.identity);
                                        }}
                                      />
                                    </Col>
                                    <Col>
                                      <MaterialIcon
                                        icon="delete"
                                        color="#E63E35"
                                        size={20}
                                        className="cursor-pointer"
                                        onClick={() => {
                                          setShowDeleteBranchModal(true);
                                          setBranchId(branch.identity);
                                        }}
                                      />
                                    </Col>
                                  </Row>
                                </Col>
                              </Row>
                            );
                          })
                      ) : (
                        <Row className="container-dash mx-0">
                          <Col className="text-center text-color-2 font-size-14">
                            There are no branches
                          </Col>
                        </Row>
                      )}
                    </Col>
                  </Row>
                </>
              )}
              {selectedItem.id === 3 && (
                <Row className="row-cols-auto gap-3 mt-4 mx-0">
                  <Col
                    className={`theme-card rounded-3 py-4 px-5 cursor-pointer border border-2 ${
                      theme === "dark" ? `border-primary` : `border-none`
                    } ${styles.themeCard}`}
                    onClick={() => dispatch(setTheme("dark"))}
                  >
                    <Row className="justify-content-center">
                      <Col className="col-auto">
                        <MaterialIcon
                          icon="dark_mode"
                          color="var(--color-theme-icon)"
                        />
                      </Col>
                    </Row>
                    <Row className="mt-1">
                      <Col className="title-2">Dark Mode</Col>
                    </Row>
                    {theme === "dark" && (
                      <Row className="justify-content-center mt-2">
                        <Col className="col-auto text-white bg-primary rounded-4">
                          Active
                        </Col>
                      </Row>
                    )}
                  </Col>
                  <Col
                    className={`theme-card rounded-3 py-4 px-5 cursor-pointer border border-2 ${
                      theme === "light" ? `border-primary` : `border-none`
                    } ${styles.themeCard}`}
                    onClick={() => dispatch(setTheme("light"))}
                  >
                    <Row className="justify-content-center">
                      <Col className="col-auto">
                        <MaterialIcon
                          icon="light_mode"
                          color="var(--color-theme-icon)"
                        />
                      </Col>
                    </Row>
                    <Row className="mt-1">
                      <Col className="title-2">Light Mode</Col>
                    </Row>
                    {theme === "light" && (
                      <Row className="justify-content-center mt-2">
                        <Col className="col-auto text-white bg-primary rounded-4">
                          Active
                        </Col>
                      </Row>
                    )}
                  </Col>
                  <Col
                    className={`theme-card rounded-3 py-4 px-5 cursor-pointer border border-2 ${
                      theme === "system" ? `border-primary` : `border-none`
                    } ${styles.themeCard}`}
                    onClick={() => dispatch(setTheme("system"))}
                  >
                    <Row className="justify-content-center">
                      <Col className="col-auto">
                        <MaterialIcon
                          icon="desktop_windows"
                          color="var(--color-theme-icon)"
                        />
                      </Col>
                    </Row>
                    <Row className="mt-1">
                      <Col className="title-2">System Default</Col>
                    </Row>
                    {theme === "system" && (
                      <Row className="justify-content-center mt-2">
                        <Col className="col-auto text-white bg-primary rounded-4">
                          Active
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>
              )}
            </div>
          </Col>
        </Row>
        <SpinnerModal
          show={
            isFetchingAdminConfiguration ||
            isFetchingUserConfiguration ||
            (selectedItem.id === 2 && isFetchingBranches)
          }
        />
      </div>
      <BrandPriorityChangeModal
        show={showBrandPriorityModal}
        onClose={() => setShowBrandPriorityModal(false)}
        onCancel={() => setShowBrandPriorityModal(false)}
        onConfirm={() => {
          onPreferenceChange(
            EConfigurations.BRAND_PRIORITY_LIST,
            brands.map((brand, index) => {
              return {
                brand: brand.brand,
                priority: index + 1,
              };
            }),
          );
          setShowBrandPriorityModal(false);
        }}
        brands={brands}
        setBrands={setBrands}
      />
      <AddBranchModal
        show={showAddBranchModal}
        onClose={() => setShowAddBranchModal(false)}
        onCancel={() => setShowAddBranchModal(false)}
        branchId={branchId}
        setIsUpdateCurrentState={setIsUpdateCurrentState}
      />
      <DeleteBranchModal
        show={showDeleteBranchModal}
        onClose={() => setShowDeleteBranchModal(false)}
        onCancel={() => setShowDeleteBranchModal(false)}
        branchId={branchId}
        setIsUpdateCurrentState={setIsUpdateCurrentState}
      />
    </>
  );
};

export default Settings;
